import React, { useEffect, useRef, useCallback } from 'react';
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Point } from 'ol/geom';
import { Feature } from 'ol';
import { Style, Fill, Stroke, Circle } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import { toFeatureCollection, fitMapToSource } from '../../utils/mapGeoJson';
import 'ol/ol.css';

const markerStyle = new Style({
  image: new Circle({
    radius: 10,
    fill: new Fill({ color: '#2563EB' }),
    stroke: new Stroke({ color: '#FFFFFF', width: 3 }),
  }),
});

const polygonStyle = new Style({
  fill: new Fill({ color: 'rgba(13, 148, 136, 0.22)' }),
  stroke: new Stroke({ color: '#0F766E', width: 2 }),
});

const DEFAULT_CENTER = [-47.93, -15.78];

function readPolygonFeatures(value) {
  const fc = toFeatureCollection(value);
  if (!fc) return [];
  try {
    return new GeoJSON().readFeatures(fc, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
  } catch {
    return [];
  }
}

/**
 * Mapa simples: um marcador; em modo editável, clique define lat/lng (WGS84).
 */
const MapLocationPicker = ({
  latitude,
  longitude,
  areaGeojson,
  onChange,
  editable = true,
  height = '280px',
  defaultCenter = DEFAULT_CENTER,
  defaultZoom = 5,
  className = '',
}) => {
  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const vectorSourceRef = useRef(null);
  const polygonSourceRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const syncMarker = useCallback(
    (lat, lng) => {
      const src = vectorSourceRef.current;
      if (!src) return;
      src.clear();
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
      const f = new Feature({
        geometry: new Point(fromLonLat([lng, lat])),
      });
      f.setStyle(markerStyle);
      src.addFeature(f);
    },
    []
  );

  useEffect(() => {
    if (!mapRef.current) return;

    const vectorSource = new VectorSource();
    vectorSourceRef.current = vectorSource;
    const polygonSource = new VectorSource();
    polygonSourceRef.current = polygonSource;

    const vectorLayer = new VectorLayer({
      source: vectorSource,
    });
    const polygonLayer = new VectorLayer({
      source: polygonSource,
      style: polygonStyle,
    });

    const center =
      longitude != null && latitude != null && !Number.isNaN(longitude) && !Number.isNaN(latitude)
        ? [longitude, latitude]
        : defaultCenter;

    const map = new Map({
      target: mapRef.current,
      layers: [new TileLayer({ source: new OSM() }), polygonLayer, vectorLayer],
      view: new View({
        center: fromLonLat(center),
        zoom:
          longitude != null && latitude != null && !Number.isNaN(longitude) && !Number.isNaN(latitude)
            ? 14
            : defaultZoom,
      }),
    });

    mapInst.current = map;

    const onClick = (evt) => {
      if (!editable) return;
      const [lng, lat] = toLonLat(evt.coordinate);
      onChangeRef.current?.({ latitude: lat, longitude: lng });
    };
    map.on('click', onClick);

    return () => {
      map.setTarget(null);
      mapInst.current = null;
      vectorSourceRef.current = null;
      polygonSourceRef.current = null;
    };
  }, [editable, defaultCenter, defaultZoom]);

  useEffect(() => {
    syncMarker(
      latitude != null ? Number(latitude) : null,
      longitude != null ? Number(longitude) : null
    );
    const polySrc = polygonSourceRef.current;
    const map = mapInst.current;
    if (polySrc) {
      polySrc.clear();
      try {
        polySrc.addFeatures(readPolygonFeatures(areaGeojson));
      } catch {
        polySrc.clear();
      }
    }
    if (!map) return;
    map.updateSize();
    if (polySrc && !polySrc.isEmpty()) {
      const fitted = fitMapToSource(map, polySrc);
      if (!fitted) {
        requestAnimationFrame(() => {
          map.updateSize();
          fitMapToSource(map, polySrc);
        });
      }
      return;
    }
    if (
      longitude != null &&
      latitude != null &&
      !Number.isNaN(Number(longitude)) &&
      !Number.isNaN(Number(latitude))
    ) {
      map.getView().setCenter(fromLonLat([Number(longitude), Number(latitude)]));
      map.getView().setZoom(14);
    }
  }, [latitude, longitude, areaGeojson, syncMarker]);

  return (
    <div
      ref={mapRef}
      className={`w-full overflow-hidden rounded-lg border border-gray-200 ${className}`.trim()}
      style={{ height }}
      role="presentation"
    />
  );
};

export default MapLocationPicker;
